import React, { useState, useEffect } from 'react';
import loadRazorpay from '../context/loadRazorpay';
import { styled } from '@mui/material/styles';
import posthog from 'posthog-js';
import {
  Box,
  Stack,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  Paper,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Constellation1 from '../assets/images/consti_1.svg';
import Constellation2 from '../assets/images/consti_2.svg';
import Constellation3 from '../assets/images/consti_3.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BackgroundDiv } from '../components/StyledComponents';
import BreadcrumbNavbar from '../components/BreadcrumbNavbar';
import { useNavigate } from 'react-router-dom';

const BLUE_COLOR = '#647ACB';

const plans = {
  INR: [
    {
      title: 'Jyoti',
      subTitle: 'Ask one accurate question',
      image: Constellation1,
      price: '₹99',
      per: '/10 Token',
      btnColor: '#252525',
      features: [
        'Get 10 Token Ideal for one-time curiosity',
        'Cost Just ₹9.90 per Token',
        'Instant Access',
        'Validity Lifetime',
        'No discount (Base pack)',
        'Secure & Private Conversations',
      ],
      currency: 'INR',
    },
    {
      title: 'Chandra',
      subTitle: 'Regular seekers of guidance',
      image: Constellation2,
      price: '₹499',
      per: '/60 Token',
      btnColor: BLUE_COLOR,
      badge: 'Save 16%',
      features: [
        'Unlock clarity with 6 questions at a discounted rate',
        'Cost Just ₹8.31 per Token',
        'Instant Access',
        'Validity Lifetime',
        'Priority Response time',
        'Secure & Private Conversations',
      ],
      currency: 'INR',
    },
    {
      title: 'Brahma',
      subTitle: 'Need consistent life guidance',
      image: Constellation3,
      price: '₹999',
      per: '/122 Token',
      btnColor: '#252525',
      badge: 'Save 10%',
      features: [
        'Unlock clarity with 14 questions at a discounted rate',
        'Cost Just ₹8.19 per Token',
        'Instant Access',
        'Validity Lifetime',
        'Fastest Response time',
        'Secure & Private Conversations',
      ],
      currency: 'INR',
    },
  ],
  USD: [
    {
      title: 'Jyoti',
      subTitle: 'Ask one accurate question',
      image: Constellation1,
      price: '$2.99',
      per: '/10 Token',
      btnColor: '#252525',
      features: [
        'Get 10 Token Ideal for one-time curiosity',
        'Cost Just $0.12 per Token',
        'Instant Access',
        'Validity Lifetime',
        'No discount (Base pack)',
        'Secure & Private Conversations',
      ],
      currency: 'USD',
    },
    {
      title: 'Chandra',
      subTitle: 'Regular seekers of guidance',
      image: Constellation2,
      price: '$8.99',
      per: '/60 Token',
      btnColor: BLUE_COLOR,
      badge: 'Save 16%',
      features: [
        'Unlock clarity with 6 questions at a discounted rate',
        'Cost Just $0.10 per Token',
        'Instant Access',
        'Validity Lifetime',
        'Priority Response time',
        'Secure & Private Conversations',
      ],
      currency: 'USD',
    },
    {
      title: 'Brahma',
      subTitle: 'Need consistent life guidance',
      image: Constellation3,
      price: '$13.99',
      per: '/122 Token',
      btnColor: '#252525',
      badge: 'Save 10%',
      features: [
        'Unlock clarity with 14 questions at a discounted rate',
        'Cost Just $0.098 per Token',
        'Instant Access',
        'Validity Lifetime',
        'Fastest Response time',
        'Secure & Private Conversations',
      ],
      currency: 'USD',
    },
  ],
};

const CardContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  padding: '24px 20px',
  position: 'relative',
  maxWidth: 320,
  minWidth: 300,
  background: theme.palette.mode === 'dark' ? '#2e2e2e' : '#fcfcfc',
  boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
}));

const Badge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  right: 20,
  backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1c1c1c',
  color: theme.palette.mode === 'dark' ? '#000000' : '#ffffff',
  borderRadius: '8px',
  padding: '2px 10px',
  fontSize: '12px',
  fontWeight: 600,
  zIndex: 2,
}));

const PriceText = styled('span')(({ theme }) => ({
  fontSize: '40px',
  fontWeight: 'bold',
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
}));

const PerTokenText = styled('span')(() => ({
  fontSize: '14px',
  fontStyle: 'italic',
  marginLeft: 4,
}));

const StyledButton = styled(Button)(({ theme, bgcolor }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  padding: '10px 0',
  fontWeight: 600,
  fontSize: '16px',
  color:
    theme.palette.mode === 'dark'
      ? bgcolor === BLUE_COLOR
        ? '#ffffff'
        : '#252525'
      : '#ffffff',
  backgroundColor:
    theme.palette.mode == 'dark'
      ? bgcolor === BLUE_COLOR
        ? bgcolor
        : '#ffffff'
      : bgcolor,
  '&:hover': {
    backgroundColor:
      theme.palette.mode == 'dark'
        ? bgcolor === BLUE_COLOR
          ? bgcolor
          : '#ffffff' + 'aa'
        : bgcolor + 'aa',
  },
}));

const Divider = styled('div')(() => ({
  margin: '24px 0 16px',
  height: '2px',
  width: '100%',
  background: 'linear-gradient(to right, #75B5FF, #04346D)',
}));

const FeatureText = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '12px',
  fontSize: '14px',
  fontStyle: 'Inter, sans-serif',
  fontWeight: '400',
}));

const ImageBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '20px',
  top: '20px',
  zIndex: 0,
  opacity: theme.palette.mode === 'dark' ? '50%' : '100%',
}));

const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 32,
  padding: 7,
  panddingLeft: 5,
  paddingRight: 5,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(30px)',
      '& + .MuiSwitch-track': {
        background: 'linear-gradient(90deg, #75B5FF 0%, #04346D 100%)',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgba(0, 35, 11, 0.2)',
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const PlanCard = ({ plan }) => {
  const navigate = useNavigate();
  const login =
    localStorage.getItem('islogin') &&
    localStorage.getItem('islogin') === 'true';

  return (
    <Box
      sx={{
        position: 'relative',
        p: '2px', // Thickness of the border
        borderRadius: '12px',
        background: 'linear-gradient(90deg, #75B5FF, #04346D)', // Your gradient
      }}
    >
      <CardContainer>
        {plan.badge && <Badge>{plan.badge}</Badge>}
        <ImageBox>
          <img src={plan.image} alt="Constellation" width={100} />
        </ImageBox>
        <Typography
          variant="h3"
          fontWeight={600}
          mt={1}
          sx={{ zIndex: 20, marginBottom: '5px !important' }}
        >
          {plan.title}
        </Typography>
        <Typography
          variant="h5"
          mb={2}
          sx={{ position: 'relative', zIndex: 1, marginTop: '5px !important' }}
        >
          {plan.subTitle}
        </Typography>
        <Box my={5}>
          <PriceText>{plan.price}</PriceText>
          <PerTokenText>{plan.per}</PerTokenText>
        </Box>
        <StyledButton
          bgcolor={plan.btnColor}
          fullWidth
          onClick={() => {
            if (!login) {
              navigate('/signin');
              return;
            }
            const numericAmount = parseFloat(
              plan.price.replace(/[^0-9.]/g, '')
            );
            posthog.capture('token_pack_purchase_initiated', {
              plan_name: plan.title,
              price: plan.price,
              currency: plan.currency,
            });
            loadRazorpay(numericAmount, plan.title, plan.currency);
          }}
        >
          Buy now
        </StyledButton>
        <Divider />
        <Typography variant="h5" gutterBottom>
          Features
        </Typography>
        <Box>
          {plan.features.map((f, i) => (
            <FeatureText
              key={i}
              sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 16, mt: 0.25 }} />
              {f}
            </FeatureText>
          ))}
        </Box>
      </CardContainer>
    </Box>
  );
};

export const PricingContainer = () => {
  const [currency, setCurrency] = useState('INR');

  return (
    <Box
      px={2}
      py={4}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Typography
        fontSize={50}
        textAlign="center"
        fontWeight="700"
        mb={1}
        sx={{ maxWidth: '700px' }}
      >
        Select a <span style={{ color: '#ff9800' }}>Token</span> Pack to Unlock{' '}
        <span style={{ color: BLUE_COLOR }}>Vedic</span> Guidance
      </Typography>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        mt={2}
        mb={4}
      >
        <Paper
          width="fit-content"
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1,
            borderRadius: '10px',
          }}
        >
          <Typography sx={{ px: 2 }}>USD US$</Typography>
          <CustomSwitch
            checked={currency === 'INR'}
            onChange={() => setCurrency(currency === 'INR' ? 'USD' : 'INR')}
          />
          <Typography sx={{ px: 2 }}>INR IN₹</Typography>
        </Paper>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={6}
        justifyContent="center"
      >
        {plans[currency].map((plan, index) => (
          <PlanCard key={index} plan={plan} />
        ))}
      </Stack>
    </Box>
  );
};
